import React from 'react';
import { act } from 'react-dom/test-utils';
import { render, fireEvent } from '@testing-library/react';
import { unmountComponentAtNode } from 'react-dom';
import Slider, { setSliderHandlePosition } from '../Slider';

describe('Slider Component', () => {
  const props = {
    indexPoints: [
      {
        position: 0.4,
        iconName: 'mark-in',
        name: 'mark-in',
      },
      {
        position: 0.6,
        iconName: 'mark-out',
        name: 'mark-out',
      },
      {
        position: 0.5,
        name: 'index-1',
        iconName: 'radiobutton-checked',
        connectedTo: 'index-2',
      },
      {
        position: 0.3,
        name: 'index-2',
        iconName: 'radiobutton-checked',
        connectedTo: 'index-1',
      },
      {
        position: 0.5,
        name: 'index-3',
        iconName: 'radiobutton-checked',
        connectedTo: 'index-4',
      },
      {
        position: 0.3,
        name: 'index-4',
        iconName: 'radiobutton-checked',
        connectedTo: 'index-3',
      },
    ],
    min: 0,
    max: 1,
    animate: true,
    scale: 1,
    showSliderHandle: true,
    onChange: () => jest.fn(),
  };
  it('should be defined', () => {
    const { container } = render(<Slider />);
    expect(container).toBeDefined();
  });

  it('changing value', () => {
    const onChange = jest.fn();
    const { getByTestId } = render(
      <Slider
        onChange={onChange}
        min='0.1'
        max='0.8'
        animate={true}
        showSliderHandle={true}
        scale={1}
      />
    );
    fireEvent.mouseDown(getByTestId('core-slider-padding'));
    expect(onChange).toHaveBeenCalled();
  });

  it('componentWillUnmount should be called on unmount', () => {
    const spy = jest.spyOn(Slider.prototype, 'componentWillUnmount');
    const { unmount } = render(<Slider />);
    unmount();
    expect(spy).toHaveBeenCalled();
  });

it('mouseMove should be called', () => {
    const slideSpy = jest.spyOn(Slider.prototype, 'slide');
    const onMouseUpSpy = jest.spyOn(Slider.prototype, 'onMouseUp');
    const { getByTestId } = render(<Slider />);
    const testNode = getByTestId('core-slider-padding');
    fireEvent.mouseDown(testNode);
    fireEvent.mouseMove(testNode);
    expect(slideSpy).toHaveBeenCalled();
    fireEvent.mouseUp(testNode);
    expect(onMouseUpSpy).toHaveBeenCalled();
  });

  describe('calculatePosition function', () => {
    it('gives right output', () => {
      const container = document.createElement('div');
      const sliderRef = React.createRef();
      const onChange = jest.fn();
      
      act(() => {
        render(<Slider ref={sliderRef} onChange={onChange} {...props} />, container);
      });
      
      const clientWidth = 150;
      Object.defineProperty(sliderRef.current.sliderWrapper, 'clientWidth', {
        value: clientWidth,
      });

      expect(sliderRef.current.calculatePosition(100)).toBe(100);
      expect(sliderRef.current.calculatePosition(200)).toBe(150);
      expect(sliderRef.current.calculatePosition(-0)).toBe(-0);
      expect(sliderRef.current.calculatePosition(-150)).toBe(0);

      act(() => {
        unmountComponentAtNode(container);
      });
    });
  });

  describe('calculatePositionWithValue function', () => {
    it('gives the right output', () => {
      const container = document.createElement('div');
      const sliderRef = React.createRef();
      
      act(() => {
        render(<Slider {...props} ref={sliderRef} />, container);
      });
      
      const clientWidth = 100;
      Object.defineProperty(sliderRef.current.sliderWrapper, 'clientWidth', {
        value: clientWidth,
      });
      
      expect(sliderRef.current.calculatePositionWithValue(120)).toBe(100);
      expect(sliderRef.current.calculatePositionWithValue(0.2)).toBe(20);
      expect(sliderRef.current.calculatePositionWithValue(-1.5)).toBe(0);
      
      act(() => {
        unmountComponentAtNode(container);
      });
    });
  });

  describe('setSliderHandlePosition', () => {
    it('gives right output when min is 0 & max is 1', () => {
      const clientWidth = 150;
      const min = 0;
      const max = 1;
      
      let position = 140;
      expect(setSliderHandlePosition(position, clientWidth, min, max)).toBe(150);
      
      position = 160;
      expect(setSliderHandlePosition(position, clientWidth, min, max)).toBe(150);
    });
    
    it('gives right output when min is not 0 or max is not 1', () => {
      const clientWidth = 130;
      const min = 0.4;
      const max = 1;
      
      let position = 140;
      expect(setSliderHandlePosition(position, clientWidth, min, max)).toBe(30246.666666666664);
      
      position = 160;
      expect(setSliderHandlePosition(position, clientWidth, min, max)).toBe(34580);
    });
    
    it('gives right output when min is not 0 and max is not 1', () => {
      const clientWidth = 70;
      const min = 0.4;
      const max = 0.8;
      
      let position = 140;
      expect(setSliderHandlePosition(position, clientWidth, min, max)).toBe(24429.999999999996);
      
      position = 160;
      expect(setSliderHandlePosition(position, clientWidth, min, max)).toBe(27929.999999999996);
    });
  });

  describe('onHoverEnter', () => {
    it('will add mouseMove event listeners', () => {
      const { getByTestId } = render(<Slider {...props} showHoveredArea={true} disables={false} />);
      const testNode = getByTestId('core-slider-padding');
      const emitChangeSpy = jest.spyOn(Slider.prototype, 'emitChange');
      const animateHoveredSpy = jest.spyOn(Slider.prototype, 'emitChange');
      fireEvent.mouseEnter(testNode);
      expect(emitChangeSpy).toHaveBeenCalled();
      fireEvent.mouseMove(testNode);
      expect(animateHoveredSpy).toHaveBeenCalled();
    });
  });

  describe('onHoverLeave', () => {
    it('will remove mouseMove event listeners ', () => {
      const { getByTestId } = render(<Slider {...props} showHoveredArea={true} disables={false} />);
      const element = getByTestId('core-slider-padding');
      const mockAnimateHovered = jest.fn();
      const mockAnimateTooltip = jest.fn();
      element.animateHovered = mockAnimateHovered;
      element.animateTooltip = mockAnimateTooltip;
      fireEvent.mouseEnter(element);
      fireEvent.mouseLeave(element);
      expect(mockAnimateHovered).not.toHaveBeenCalled();
      expect(mockAnimateTooltip).not.toHaveBeenCalled();
    });
  });

  describe('componentDidUpdate', () => {
    it('will update sliderHandleLeftStyle state on value prop change', () => {
      const container = document.createElement('div');
      const sliderRef = React.createRef();
      let reRender;
      
      act(() => {
        const { rerender } = render(<Slider {...props} value={0.03} ref={sliderRef} />, container);
        reRender = rerender;
      });
      Object.defineProperty(sliderRef.current.sliderWrapper, 'clientWidth', {
        value: 100,
      });
      reRender(<Slider {...props} value={0.8} ref={sliderRef} />);
      expect(sliderRef.current.state.sliderHandleLeftStyle).toBe(80);

      act(() => {
        unmountComponentAtNode(container);
      });
    });

    it('will update indexPoints state on indexPoints prop change', () => {
      const container = document.createElement('div');
      const sliderRef = React.createRef();
      let reRender;
      act(() => {
        const { rerender } = render(<Slider {...props} value={0.03} ref={sliderRef} />, container);
        reRender = rerender;
      });
      const updatedIndexPoints = [
        {
          position: 0.4,
          iconName: 'mark-in',
          name: 'mark-in',
        },
        {
          position: 0.6,
          iconName: 'mark-out',
          name: 'mark-out',
        },
      ];
      reRender(<Slider {...props} indexPoints={updatedIndexPoints} ref={sliderRef} />);
      expect(sliderRef.current.state.indexPoints).toEqual(updatedIndexPoints);

      act(() => {
        unmountComponentAtNode(container);
      });
    });
  });

  describe('snaphsot', () => {
    it('should match snapshot', () => {
      const { asFragment } = render(<Slider {...props} />);
      expect(asFragment()).toMatchSnapshot();
    });
    
    it('should match snapshot when disabled', () => {
      const { asFragment } = render(<Slider {...props} disabled={true} />);
      expect(asFragment()).toMatchSnapshot();
    });
  });
});
